import m from "mithril";
import { getAuthToken, getUserData, getSyncUserData, getMithril, postMithril, showToast, formatDateWIB } from "../utils.js";

const SavingsDepositsCoordinator = {
    customers: [],
    selectedCustomer: null,
    customerAccounts: [],
    selectedSavingsAccount: null,
    transactions: [],
    depositAmount: '',
    depositDescription: '',
    loading: false,
    searching: false,
    searchTerm: '',
    currentView: 'customers', // 'customers', 'accounts', 'deposit'

    oninit() {
        this.fetchCustomers();
    },

    async fetchCustomers() {
        this.searching = true;
        try {
            const result = await getMithril('/api/savings-deposits-coordinator', { component: this });

            if (result.success) {
                this.customers = result.data;
                console.log('Customers data:', this.customers);
                console.log('First customer object:', this.customers[0]);
                console.log('Customer properties:', Object.keys(this.customers[0] || {}));
            } else {
                throw new Error(result.error || 'Failed to load customers');
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            showToast('Gagal memuat data nasabah: ' + error.message, 'error');
        } finally {
            this.searching = false;
            m.redraw();
        }
    },

    filterCustomers() {
        if (!this.searchTerm) return this.customers;

        const term = this.searchTerm.toLowerCase();
        return this.customers.filter(customer =>
            customer.customer_name.toLowerCase().includes(term) ||
            customer.account_number.toLowerCase().includes(term) ||
            customer.customer_no_reg.toLowerCase().includes(term)
        );
    },

    async selectCustomer(customer) {
        this.selectedCustomer = customer;
        this.currentView = 'accounts';
        // Clear previous data
        this.customerAccounts = [];
        this.transactions = [];
        m.redraw();

        // Fetch data asynchronously
        try {
            await Promise.all([
                this.fetchCustomerAccounts(customer.customer_id),
                this.fetchCustomerTransactions(customer.customer_id)
            ]);
        } finally {
            // Always redraw after both operations complete (whether successful or not)
            m.redraw();
        }
    },

    async selectAccount(account) {
        this.selectedSavingsAccount = account;
        this.currentView = 'deposit';
        this.depositAmount = '';
        this.depositDescription = '';
        m.redraw();
    },

    goBack() {
        if (this.currentView === 'deposit') {
            this.currentView = 'accounts';
            this.selectedSavingsAccount = null;
            this.depositAmount = '';
            this.depositDescription = '';
        } else if (this.currentView === 'accounts') {
            this.currentView = 'customers';
            this.selectedCustomer = null;
            this.customerAccounts = [];
            this.transactions = [];
        }
        m.redraw();
    },

    async fetchCustomerAccounts(customerId) {
        console.log('Frontend: Calling fetchCustomerAccounts with customerId:', customerId);
        try {
            const apiUrl = `/api/savings-accounts?customerId=${customerId}`;
            console.log('Frontend: Making API call to:', apiUrl);

            const result = await getMithril(apiUrl, { component: this });
            console.log('Frontend: API response data:', result);

            if (result.success) {
                this.customerAccounts = result.data;
                console.log('Frontend: Set customerAccounts to:', this.customerAccounts);
            } else {
                console.error('Frontend: API returned success=false:', result);
            }
        } catch (error) {
            console.error('Error fetching customer accounts:', error);
            showToast('Gagal memuat data rekening', 'error');
        }
    },

    async fetchCustomerTransactions(customerId) {
        try {
            // Use the existing savings API with customer filter
            const result = await getMithril(`/api/savings?customerName=${encodeURIComponent(this.selectedCustomer.customer_name)}`, { component: this });
            if (result.success) {
                this.transactions = result.data;
            }
        } catch (error) {
            console.error('Error fetching customer transactions:', error);
            showToast('Gagal memuat data transaksi', 'error');
        }
    },

    async submitDeposit() {
        if (!this.selectedSavingsAccount || !this.depositAmount || !this.depositDescription) {
            showToast('Semua field harus diisi', 'warning');
            return;
        }

        const amount = parseFloat(this.depositAmount);
        if (amount <= 0) {
            showToast('Jumlah setoran harus lebih dari 0', 'warning');
            return;
        }

        this.loading = true;
        try {
            const result = await postMithril('/api/savings-deposits-coordinator', {
                savings_account_id: this.selectedSavingsAccount.id,
                deposit_amount: amount,
                description: this.depositDescription
            }, { component: this });

            if (result.success) {
                showToast('Setoran berhasil dicatat', 'success');
                // Go back to accounts view and refresh data
                this.goBack();
                await this.fetchCustomerAccounts(this.selectedCustomer.customer_id);
                await this.fetchCustomerTransactions(this.selectedCustomer.customer_id);
            } else {
                throw new Error(result.error || 'Failed to process deposit');
            }
        } catch (error) {
            console.error('Error processing deposit:', error);
            showToast('Gagal memproses setoran: ' + error.message, 'error');
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    view() {
        const userData = getSyncUserData();

        return m("div", { class: "p-6 max-w-7xl mx-auto" }, [
            // Header with navigation
            m("div", { class: "mb-6" }, [
                m("div", { class: "flex items-center justify-between" }, [
                    m("div", [
                        m("h1", { class: "text-3xl font-bold text-gray-800 dark:text-white mb-2" }, "Setoran Tabungan"),
                        m("p", { class: "text-gray-600 dark:text-gray-300" }, "Catat setoran nasabah ke rekening tabungan")
                    ]),
                    this.currentView !== 'customers' ? m("button", {
                        class: "px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200",
                        onclick: () => this.goBack()
                    }, [
                        m("i", { class: "ri-arrow-left-line mr-2" }),
                        "Kembali"
                    ]) : null
                ]),

                // Breadcrumb
                m("div", { class: "flex items-center text-sm text-gray-500 dark:text-gray-400 mt-2" }, [
                    m("span", { class: "cursor-pointer hover:text-blue-600", onclick: () => { this.currentView = 'customers'; this.selectedCustomer = null; this.customerAccounts = []; this.transactions = []; m.redraw(); } }, "Nasabah"),
                    this.currentView === 'accounts' || this.currentView === 'deposit' ? [
                        m("span", { class: "mx-2" }, ">"),
                        m("span", { class: "font-medium text-gray-700 dark:text-gray-300" }, this.selectedCustomer?.customer_name)
                    ] : null,
                    this.currentView === 'deposit' ? [
                        m("span", { class: "mx-2" }, ">"),
                        m("span", { class: "font-medium text-gray-700 dark:text-gray-300" }, "Input Setoran")
                    ] : null
                ])
            ]),

            // Customers View
            this.currentView === 'customers' ? this.renderCustomersView() :
                this.currentView === 'accounts' ? this.renderAccountsView() :
                    this.renderDepositForm()
        ]);
    },

    renderCustomersView() {
        const filteredCustomers = this.filterCustomers();

        return m("div", [
            // Search Bar
            m("div", { class: "mb-6" }, [
                m("div", { class: "relative" }, [
                    m("input", {
                        type: "text",
                        placeholder: "Cari nasabah berdasarkan nama, nomor rekening, atau nomor registrasi...",
                        class: "w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        value: this.searchTerm,
                        oninput: (e) => { this.searchTerm = e.target.value; },
                        disabled: this.searching
                    }),
                    m("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                        m("i", { class: "ri-search-line text-gray-400 dark:text-gray-500" })
                    ]),
                    this.searching ? m("div", { class: "absolute inset-y-0 right-0 pr-3 flex items-center" }, [
                        m("i", { class: "ri-loader-4-line animate-spin text-blue-500" })
                    ]) : null
                ])
            ]),

            m("div", { class: "grid gap-4 md:grid-cols-2 lg:grid-cols-3" },
                this.searching ? m("div", { class: "col-span-full text-center py-8" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-3xl text-blue-500 mb-4" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Memuat data nasabah...")
                ]) : filteredCustomers.length === 0 ? m("div", { class: "col-span-full text-center py-8" }, [
                    m("i", { class: "ri-file-list-3-line text-3xl text-gray-400 dark:text-gray-500 mb-4" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Tidak ada nasabah dengan rekening tabungan aktif")
                ]) : filteredCustomers.map(customer =>
                    m("div", {
                        class: "bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
                        onclick: () => this.selectCustomer(customer)
                    }, [
                        m("div", { class: "flex justify-between items-start mb-3" }, [
                            m("div", [
                                m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-1" },
                                    customer.customer_name
                                ),
                                m("p", { class: "text-sm text-gray-600 dark:text-gray-300" },
                                    `No. Reg: ${customer.customer_no_reg || 'N/A'}`
                                )
                            ]),
                            m("div", { class: "text-right" }, [
                                m("p", { class: "text-sm font-medium text-green-600 dark:text-green-400" },
                                    `Rp ${(customer.balance && !isNaN(customer.balance)) ? parseFloat(customer.balance).toLocaleString('id-ID') : '0'}`
                                ),
                                m("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, "Saldo")
                            ])
                        ]),
                        m("div", { class: "flex justify-between items-center" }, [
                            m("div", [
                                m("p", { class: "text-sm text-gray-600 dark:text-gray-300" },
                                    `Rekening: ${customer.account_number || 'N/A'}`
                                ),
                                customer.account_name ? m("p", { class: "text-xs text-gray-500 dark:text-gray-400" },
                                    `Nama: ${customer.account_name}`
                                ) : null
                            ]),
                            m("div", { class: "text-right" }, [
                                m("span", {
                                    class: "px-2 py-1 text-xs rounded-full " +
                                        (customer.status === 'active' ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200" :
                                            "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200")
                                }, customer.status === 'active' ? 'Aktif' : 'Tidak Aktif')
                            ])
                        ])
                    ])
                )
            )
        ]);
    },

    renderAccountsView() {
        return m("div", { class: "grid grid-cols-1 lg:grid-cols-2 gap-6" }, [
            // Accounts Section
            m("div", [
                m("h2", { class: "text-xl font-semibold text-gray-800 dark:text-white mb-4" }, "Rekening Tabungan"),
                m("div", { class: "space-y-3" },
                    this.customerAccounts.length === 0 ? m("div", { class: "text-center py-8 bg-white dark:bg-gray-800 rounded-lg" }, [
                        m("i", { class: "ri-bank-line text-3xl text-gray-400 dark:text-gray-500 mb-4" }),
                        m("p", { class: "text-gray-600 dark:text-gray-300" }, "Tidak ada rekening aktif")
                    ]) : this.customerAccounts.map(account =>
                        m("div", {
                            class: "bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20",
                            onclick: () => this.selectAccount(account)
                        }, [
                            m("div", { class: "flex justify-between items-start mb-2" }, [
                                m("div", [
                                    m("h4", { class: "font-semibold text-gray-800 dark:text-white" },
                                        account.account_number
                                    ),
                                    account.account_name ? m("p", { class: "text-sm text-gray-600 dark:text-gray-300" },
                                        account.account_name
                                    ) : null
                                ]),
                                m("div", { class: "text-right" }, [
                                    m("p", { class: "text-sm font-medium text-green-600 dark:text-green-400" },
                                        `Rp ${parseFloat(account.balance || 0).toLocaleString('id-ID')}`
                                    ),
                                    m("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, "Saldo")
                                ])
                            ]),
                            m("div", { class: "flex justify-between items-center" }, [
                                m("span", {
                                    class: "px-2 py-1 text-xs rounded-full " +
                                        (account.status === 'active' ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200" :
                                            "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200")
                                }, account.status === 'active' ? 'Aktif' : 'Tidak Aktif'),
                                m("span", { class: "text-blue-600 dark:text-blue-400 text-sm font-medium" }, "Input Setoran →")
                            ])
                        ])
                    )
                )
            ]),

            // Transactions Section
            m("div", [
                m("div", { class: "flex justify-between items-center mb-4" }, [
                    m("h2", { class: "text-xl font-semibold text-gray-800 dark:text-white" }, "Riwayat Transaksi"),
                    m("button", {
                        class: "px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200"
                    }, [
                        m("i", { class: "ri-eye-line mr-2" }),
                        "Lihat Semua"
                    ])
                ]),
                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow" },
                    this.transactions.length === 0 ? m("div", { class: "text-center py-8" }, [
                        m("i", { class: "ri-file-list-3-line text-3xl text-gray-400 dark:text-gray-500 mb-4" }),
                        m("p", { class: "text-gray-600 dark:text-gray-300" }, "Belum ada transaksi")
                    ]) : m("div", { class: "divide-y divide-gray-200 dark:divide-gray-700" },
                        this.transactions.slice(0, 5).map(transaction =>
                            m("div", { class: "p-4" }, [
                                m("div", { class: "flex justify-between items-start mb-2" }, [
                                    m("div", [
                                        m("p", { class: "font-medium text-gray-800 dark:text-white" },
                                            transaction.deskripsi || 'Setoran Tabungan'
                                        ),
                                        m("p", { class: "text-sm text-gray-600 dark:text-gray-300" },
                                            transaction.no || 'N/A'
                                        )
                                    ]),
                                    m("div", { class: "text-right" }, [
                                        m("p", { class: "font-medium text-green-600 dark:text-green-400" },
                                            `+Rp ${parseFloat(transaction.total || 0).toLocaleString('id-ID')}`
                                        ),
                                        m("p", { class: "text-xs text-gray-500 dark:text-gray-400" },
                                            formatDateWIB(transaction.tgl || transaction.created_at)
                                        )
                                    ])
                                ])
                            ])
                        )
                    )
                )
            ])
        ]);
    },

    renderDepositForm() {
        return m("div", { class: "max-w-2xl mx-auto" }, [
            // Selected Account Info
            m("div", { class: "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6" }, [
                m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-2" }, "Rekening Tujuan"),
                m("div", { class: "space-y-1" }, [
                    m("p", { class: "text-gray-700 dark:text-gray-300" },
                        m("strong", "Nasabah: "), this.selectedCustomer.customer_name
                    ),
                    m("p", { class: "text-gray-700 dark:text-gray-300" },
                        m("strong", "No. Rekening: "), this.selectedSavingsAccount.account_number
                    ),
                    this.selectedSavingsAccount.account_name ? m("p", { class: "text-gray-700 dark:text-gray-300" },
                        m("strong", "Nama Rekening: "), this.selectedSavingsAccount.account_name
                    ) : null,
                    m("p", { class: "text-gray-700 dark:text-gray-300" },
                        m("strong", "Saldo Saat Ini: "), `Rp ${parseFloat(this.selectedSavingsAccount.balance || 0).toLocaleString('id-ID')}`
                    )
                ])
            ]),

            // Deposit Form
            m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6" }, [
                m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-4" }, "Input Setoran"),
                m("div", { class: "space-y-4" }, [
                    m("div", [
                        m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Jumlah Setoran (Rp)"),
                        m("input", {
                            type: "number",
                            placeholder: "0",
                            class: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                            value: this.depositAmount,
                            oninput: (e) => { this.depositAmount = e.target.value; },
                            min: "0",
                            step: "0.01",
                            disabled: this.loading
                        })
                    ]),
                    m("div", [
                        m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Deskripsi"),
                        m("textarea", {
                            placeholder: "Masukkan deskripsi setoran...",
                            class: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                            value: this.depositDescription,
                            oninput: (e) => { this.depositDescription = e.target.value; },
                            rows: "3",
                            disabled: this.loading
                        })
                    ]),
                    m("div", { class: "flex gap-4" }, [
                        m("button", {
                            class: "flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50",
                            onclick: () => this.submitDeposit(),
                            disabled: this.loading || !this.depositAmount || !this.depositDescription
                        }, [
                            this.loading ? m("i", { class: "ri-loader-4-line animate-spin mr-2" }) : m("i", { class: "ri-add-circle-line mr-2" }),
                            this.loading ? "Memproses..." : "Catat Setoran"
                        ]),
                        m("button", {
                            class: "px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200",
                            onclick: () => this.goBack(),
                            disabled: this.loading
                        }, "Batal")
                    ])
                ])
            ])
        ]);
    }
};

export default SavingsDepositsCoordinator;